import { Box, Grid, Skeleton, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import clsx from 'clsx'
import React from 'react'
import { DEFAULT_READ_TIME } from '../../../consts'
import { useGroup } from '../../../hooks'
import { ALL_CONSUMER } from '../../../services/cognito/userGroups'
import ContentTypeBadge from '../../content-type-badge'
import { PROMOTED_CARD_TYPES } from './use-promoted-card-methods'
const multiNoWrap = lines => {
  return {
    lineClamp: lines,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical'
  }
}

const imgContainerPercentage = '45%'
const contentContainerOverflow = '30px'

const useStyles = makeStyles(({ spacing, palette, typography }) => ({
  root: {
    height: '100%'
  },
  container: {
    position: 'relative',
    width: '100%',
    height: '400px'

  },
  imgContainer: {
    height: '100%',
    width: imgContainerPercentage,
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0
  },
  contentContainer: {
    width: `calc(100% - ${imgContainerPercentage} + ${contentContainerOverflow})`,
    background: '#FFF',
    justifyContent: 'center'
    // color: palette.primary.main
  },
  image: {
    height: '100%',
    width: '100%',
    objectFit: 'cover'
  },
  cardTitle: props => ({
    fontWeight: 700,
    fontSize: '28px',
    lineHeight: '32px',
    letterSpacing: '0.6px',
    color: '#000',
    ...multiNoWrap(props.titleLines || 2)
  }),
  cardSummary: props => ({
    fontWeight: 400,
    fontSize: '18px',
    lineHeight: '24px',
    color: '#454545',
    minHeight: `calc(24px * ${props.summaryLines})`,
    ...multiNoWrap(props.summaryLines || 1)
  }),
  time: ({ isConsumer }) => ({
    color: isConsumer ? palette.grey.shade[80] : palette.primary.main,
    fontWeight: typography.fontWeightRegular
  }),
  actions: props => ({
    marginTop: spacing(props.defaultSectionMargin || 2),
    // The Grid's spacing pulls the row 8px left of the text above it; cancel
    // that so the buttons line up with the type, title and summary.
    '&&': {
      marginLeft: 0,
      width: '100%'
    }
  }),
  cardContentType: ({ isConsumer }) => ({
    // TEMP: match the medium gray used by the smaller rail cards below
    // (general-card's `info` class uses palette.text.secondary). Revert to
    // primary.main when restoring the featured-content branding.
    ...(isConsumer && { color: palette.text.secondary }),
    display: 'inline-flex',
    alignItems: 'center',
    width: 'fit-content',
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    borderRadius: 999,
    padding: spacing(0.5, 1.25),
    '& svg, & .MuiSvgIcon-root': {
      width: 14,
      height: 14,
      fontSize: 14
    },
    '& .MuiTypography-root': {
      fontWeight: typography.fontWeightMedium,
      fontSize: '0.8125rem',
      lineHeight: 1.2
    }
  })
}))

const PromotedContentCardContent = ({ content, cardType, actions, onSelect, overrides = {}, ...props }) => {
  const { defaultSectionMargin = 2, subtitleMargin = 0.5 } = props
  const { isInGroup } = useGroup()
  const classes = useStyles({ ...props, isConsumer: isInGroup(ALL_CONSUMER) })
  const isLoading = !content

  const isSecondaryCard = cardType === PROMOTED_CARD_TYPES.SECONDARY

  let contentTime = `${DEFAULT_READ_TIME} mins` // default read time
  if (content?.readTime) {
    contentTime = content?.readTime + ' mins'
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onSelect && onSelect(event)
    }
  }

  return (
    <Box
      display='flex'
      flexDirection='column'
      flex={1}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      className={clsx(classes.contentContainer, overrides.contentContainer)}
      p={isSecondaryCard ? 1 : 2}
      m={isSecondaryCard ? 1 : 0}
      role='button'
      tabIndex={0}
      aria-label={content ? `View ${content.title}` : 'Loading content'}
      sx={(theme) => ({
        cursor: 'pointer',
        // Round the hover/focus surface so the subtle tint follows the
        // outer card's corner radius instead of showing a sharp inner
        // rectangle (the Box is inset from the card via `m`, so the
        // card's overflow:hidden alone can't clip the tint).
        borderRadius: `${theme.radius.xl}px`,
        '&:focus': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px'
        },
        '&:hover': {
          backgroundColor: theme.palette.action.hover
        }
      })}
    >
      <Box
        display='flex'
        flexDirection='row'
        alignItems='center'
      >
        <Box display='flex' flex={1}>
          {isLoading
            ? <Skeleton variant='text' width={100} />
            : (
              <ContentTypeBadge
                primaryType={content?.primaryType}
                dataTest={`promoted-card-${content?.friendlyUrl}'}`}
                iconWeight='bold'
                classes={{
                  root: clsx(classes.cardContentType, overrides.cardContentType),
                  text: clsx(overrides.cardContentTypeText)
                }}
              />
              )}
        </Box>
        <Box>
          <Typography
            variant='body1'
            className={clsx(classes.time, overrides.time)}
            noWrap
            align='right'
            data-test={`card-time-${content?.friendlyUrl}'}`}
          >
            {isLoading ? <Skeleton variant='text' width={100} /> : contentTime}
          </Typography>
        </Box>
      </Box>
      <Box mt={defaultSectionMargin}>
        <Typography className={clsx(classes.cardTitle, overrides.cardTitle)}>
          {isLoading ? <Skeleton variant='text' width='100%' /> : content?.title}
        </Typography>
      </Box>
      <Box mt={subtitleMargin}>
        <Typography data-test={`card-subtitle-${content?.friendlyUrl}'}`} className={clsx(classes.cardSummary, overrides.cardSummary)}>
          {!isLoading
            ? content?.subtitle?.html
            : (
              <>
                <Skeleton width='100%' />
                <Skeleton width='70%' />
              </>
              )}
        </Typography>
      </Box>
      <Grid
        container
        className={clsx(classes.actions, overrides.actions)}
        justifyContent='space-between'
        wrap='nowrap'
        spacing={1}
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
        data-test={`card-actions-${content?.friendlyUrl}'}`}
      >
        {!isLoading
          ? (
            <>
              <Grid
                container
                item
                justifyContent='flex-start'
                spacing={1}
                wrap='nowrap'
                data-test={`card-left-actions-${content?.friendlyUrl}'}`}
              >
                {actions?.leftActions}
              </Grid>
              <Grid
                container
                item
                justifyContent='flex-end'
                spacing={1}
                wrap='nowrap'
                data-test={`card-right-actions-${content?.friendlyUrl}'}`}
              >
                {actions?.rightActions}
              </Grid>
            </>
            )
          : (
            <Box ml={0.5} width='100%'>
              <Skeleton variant='rectangular' width='40%' />
            </Box>
            )}
      </Grid>
    </Box>
  )
}

export default PromotedContentCardContent
